import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createProfileViewRequestAction } from '../../../redux/actions/userActions';
import { getSentProfileViewRequestsAction } from '../../../redux/actions/profileViewRequestActions';
import { useToast } from '../../../contexts/ToastContext';

const AboutTab = ({ 
  user, 
  isAboutExpanded, 
  setIsAboutExpanded,
  isPersonalInfoExpanded,
  setIsPersonalInfoExpanded,
  isWorkLocationsExpanded,
  setIsWorkLocationsExpanded,
  isPrivateProfile = false,
  profileUserId = null,
  isAuthenticated = true,
  onRequireAuth = null,
  isViewingOwnProfile = false,
}) => {
  const dispatch = useDispatch();
  const { success: showSuccess, error: showError } = useToast();
  const [requestLoading, setRequestLoading] = useState(false);
  
  // Get sent profile view requests from Redux
  const sentProfileViewRequests = useSelector((state) => state.user?.sentProfileViewRequests || []);

  // Fetch sent requests on mount if authenticated
  useEffect(() => {
    if (isAuthenticated && profileUserId && !isViewingOwnProfile) {
      dispatch(getSentProfileViewRequestsAction(null, 25, 0));
    }
  }, [dispatch, isAuthenticated, profileUserId, isViewingOwnProfile]);

  // Find existing profile view request for this profile owner
  const existingProfileViewRequest = useMemo(() => {
    if (!profileUserId || !sentProfileViewRequests || sentProfileViewRequests.length === 0 || isViewingOwnProfile) {
      return null;
    }
    
    const found = sentProfileViewRequests.find((request) => {
      const profileOwnerId = request.profileOwner?._id || request.profileOwner;
      return profileOwnerId && String(profileOwnerId) === String(profileUserId) && request.status === "pending";
    });
    
    return found;
  }, [sentProfileViewRequests, profileUserId, isViewingOwnProfile]);

  const handleRequestAccess = async () => {
    if (!isAuthenticated) {
      if (onRequireAuth) {
        onRequireAuth("auth_required");
      }
      return;
    }

    if (!profileUserId || isViewingOwnProfile) return;

    setRequestLoading(true);
    try {
      const result = await dispatch(createProfileViewRequestAction(profileUserId));
      if (result?.success) {
        showSuccess("Profile view request sent successfully");
        // Refresh sent requests to update button state
        await dispatch(getSentProfileViewRequestsAction(null, 25, 0));
      } else {
        showError(result?.error || "Failed to send request");
      }
    } catch (error) {
      showError("Error sending request");
    } finally {
      setRequestLoading(false);
    }
  };
  const handleAboutToggle = () => {
    setIsAboutExpanded(!isAboutExpanded);
  };

  const getDisplayContent = (content) => {
    if (!content) return '';
    
    if (isAboutExpanded) {
      return content;
    }
    
    // Estimate 5 lines based on average characters per line (approximately 80-100 chars per line)
    const estimatedCharsPerLine = 85;
    const maxChars = estimatedCharsPerLine * 4; // 5 lines
    
    if (content.length > maxChars) {
      // Find the last complete word within the limit
      const truncated = content.substring(0, maxChars);
      const lastSpaceIndex = truncated.lastIndexOf(' ');
      
      // If we found a space, truncate at the last complete word
      const cutPoint = lastSpaceIndex > maxChars * 0.8 ? lastSpaceIndex : maxChars;
      return content.substring(0, cutPoint) + '...';
    }
    
    return content;
  };

  const shouldShowViewMore = user.summary && user.summary.length > (85 * 4); // 5 lines worth

  if (isPrivateProfile) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mx-2 md:mx-0">
        {/* Left - Personal details area with lock */}
        <div className="col-span-1 bg-white rounded-lg border border-gray-200 shadow-sm p-6 flex flex-col items-center justify-center text-center">
          <svg
            className="w-8 h-8 text-amber-500 mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 11c-1.105 0-2 .895-2 2v3a2 2 0 104 0v-3a2 2 0 00-2-2z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 11V9a5 5 0 10-10 0v2M7 11h10a2 2 0 012 2v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4a2 2 0 012-2z"
            />
          </svg>
          <h3 className="text-base md:text-lg font-bold text-gray-900 font-heading mb-1">
            Private account
          </h3>
          <p className="text-xs md:text-sm text-gray-600 font-roboto-flex mb-3">
            This is a private account.
          </p>
          {!isViewingOwnProfile && (
            <button
              onClick={handleRequestAccess}
              disabled={requestLoading || existingProfileViewRequest}
              className={`w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                existingProfileViewRequest
                  ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                  : "bg-primary-700 text-white hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed"
              }`}
            >
              {requestLoading ? (
                "Sending..."
              ) : existingProfileViewRequest ? (
                "Request Pending"
              ) : (
                "Request MYST Access"
              )}
            </button>
          )}
        </div>

        {/* Right - About content hidden */}
        <div className="col-span-1 md:col-span-3 bg-white rounded-lg border border-gray-200 shadow-sm p-8 flex flex-col items-center justify-center text-center">
          <svg
            className="w-10 h-10 text-amber-500 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 11c-1.105 0-2 .895-2 2v3a2 2 0 104 0v-3a2 2 0 00-2-2z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 11V9a5 5 0 10-10 0v2M7 11h10a2 2 0 012 2v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4a2 2 0 012-2z"
            />
          </svg>
          <h2 className="text-lg md:text-xl font-bold text-gray-900 font-heading mb-2">
            Private account
          </h2>
          <p className="text-sm text-gray-600 font-roboto-flex max-w-md">
            This is a private account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mx-2 md:mx-0">
      {/* Left Card - Personal Details */}
      <div className="col-span-1 bg-white rounded-lg border border-gray-200 shadow-sm p-3 md:p-3 accordion-section">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-base md:text-lg font-bold text-gray-900 font-heading">
            Personal Details
          </h3>
          {isPrivateProfile && (
            <svg
              className="w-4 h-4 text-amber-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 11c-1.105 0-2 .895-2 2v3a2 2 0 104 0v-3a2 2 0 00-2-2z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 11V9a5 5 0 10-10 0v2M7 11h10a2 2 0 012 2v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4a2 2 0 012-2z"
              />
            </svg>
          )}
        </div>
        
        {/* Personal Information Accordion */}
        <div className="mb-2 accordion-list">
          <button
            onClick={() => setIsPersonalInfoExpanded(!isPersonalInfoExpanded)}
            className={`accordion-button w-full rounded-md text-xs md:text-sm font-bold text-gray-800 font-heading hover:bg-gray-50 transition-colors border-b border-gray-200 pb-2 ${
              isPersonalInfoExpanded ? 'expanded' : ''
            }`}
            aria-expanded={isPersonalInfoExpanded}
          >
            <span className="accordion-header">Personal Information</span>
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          {isPersonalInfoExpanded && (
            <div className="space-y-3 mt-2 pl-3">
          {/* Gender */}
          {user.gender && (
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-500 font-roboto-flex">Gender</p>
                <p className="text-sm text-gray-900 font-roboto-flex truncate">{user.gender}</p>
              </div>
            </div>
          )}

          {/* Date of Birth */}
          {user.dob && (
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-500 font-roboto-flex">Date of Birth</p>
                <p className="text-sm text-gray-900 font-roboto-flex truncate">{user.dob}</p>
              </div>
            </div>
          )}

              {/* City */}
              {user.city && (
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-500 font-roboto-flex">City</p>
                    <p className="text-sm text-gray-900 font-roboto-flex truncate">{user.city}</p>
                  </div>
                </div>
              )}

              {/* Show message if no personal information available */}
              {!user.gender && !user.dob && !user.city && (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-500 font-roboto-flex">No personal information available</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Preferred Work Locations Accordion */}
        <div className="mt-2 accordion-list">
          <button
            onClick={() => setIsWorkLocationsExpanded(!isWorkLocationsExpanded)}
            className={`accordion-button w-full rounded-md text-xs md:text-sm font-bold text-gray-800 font-heading hover:bg-gray-50 transition-colors border-b border-gray-200 pb-2 ${
              isWorkLocationsExpanded ? 'expanded' : ''
            }`}
            aria-expanded={isWorkLocationsExpanded}
          >
            <span className="accordion-header">Preferred Work Locations</span>
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          {isWorkLocationsExpanded && (
            <div className="mt-2 pl-3">
              {/* Preferred Work Locations as Chips */}
              <div className="flex flex-wrap gap-2">
                {(() => {
                  // Parse comma-separated locations and filter out country
                  const locations = user.location 
                    ? user.location.split(',').map(loc => loc.trim()).filter(loc => 
                        loc && 
                        loc.toLowerCase() !== user.country?.toLowerCase() &&
                        loc.toLowerCase() !== 'country'
                      )
                    : [];
                  
                  return locations.map((location, index) => (
                    <span 
                      key={index}
                      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200 hover:bg-green-200 transition-all duration-200 font-roboto-flex shadow-sm"
                    >
                      {location}
                    </span>
                  ));
                })()}
              </div>

              {/* Show message if no work locations available */}
              {(() => {
                const locations = user.location 
                  ? user.location.split(',').map(loc => loc.trim()).filter(loc => 
                      loc && 
                      loc.toLowerCase() !== user.country?.toLowerCase() &&
                      loc.toLowerCase() !== 'country'
                    )
                  : [];
                
                return locations.length === 0 && (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500 font-roboto-flex">No preferred work locations available</p>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>
      
      {/* Right Card - About Content */}
      <div className="col-span-1 md:col-span-3 space-y-3">
        {/* Brief Summary Section */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 md:p-4">
          <h3 className="text-xl font-bold text-gray-900 font-heading mb-4">Brief Summary</h3>
          <div className="space-y-4 text-gray-600 font-roboto-flex leading-relaxed text-sm">
            {user.bio ? (
              <div
                className="font-roboto-flex text-justify prose max-w-none"
                dangerouslySetInnerHTML={{ __html: (user.bio || '').replace(/\r?\n/g, '<br/>') }}
              />
            ) : (
              <div className="text-center text-gray-500 py-4">
                <p className="text-sm font-medium font-roboto-flex">No brief summary available</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Professional Summary Section */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 md:p-4 relative">
          <h3 className="text-xl font-bold text-gray-900 font-heading mb-4">Professional Summary</h3>
          <div className="space-y-4 text-gray-600 font-roboto-flex leading-relaxed text-sm">
            {user.summary ? (
              <div className="space-y-4">
                <div
                  className="font-roboto-flex text-justify prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: (getDisplayContent(user.summary) || '').replace(/\r?\n/g, '<br/>') }}
                />
              </div>
            ) : (
              <div className="text-center text-gray-500 py-4">
                <p className="text-sm font-medium font-roboto-flex">No professional summary available</p>
              </div>
            )}
          </div>
          
          {shouldShowViewMore && (
            <div className="flex justify-center pt-6">
              <button
                onClick={handleAboutToggle}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-full transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 font-heading text-sm"
                title={isAboutExpanded ? "View Less" : "View More"}
              >
                {isAboutExpanded ? (
                  <>
                    <span className="hidden sm:inline">View Less</span>
                    <span className="sm:hidden">Less</span>
                    <svg className="w-4 h-4 transform rotate-180 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">View More</span>
                    <span className="sm:hidden">More</span>
                    <svg className="w-4 h-4 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AboutTab;
